import React, { useState, useEffect, useRef } from 'react';
import '../styles/AddPurchaseOrderForm.css';
import { toast } from 'react-toastify';
import API_BASE_URL from '../config/apiConfig.js';
import fetchApi from '../utils/api.js';
import AirDatePicker from '../components/ui/AirDatePicker';
import AddSellerModal from '../components/modals/AddSellerModal';
import { usePermissionContext } from '../contexts/PermissionContext';
import Swal from 'sweetalert2';


const CreatableSelect = React.forwardRef(({ value, onChange, options, onAddOption, placeholder, creatable = true, onSelect, onPrevField, onTab, validateOnEnter = false }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);

  useEffect(() => {
    setSearchTerm(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newVal = e.target.value;
    setSearchTerm(newVal);
    onChange(newVal, e);
    setIsOpen(true);
    setActiveIndex(0);
  };

  const handleOptionMouseDown = (opt, e) => {
    e.preventDefault();
    onChange(opt, e);
    setSearchTerm(opt);
    setIsOpen(false);
    setActiveIndex(-1);
    // Fire onSelect callback so parent can move focus forward
    if (onSelect) setTimeout(() => onSelect(opt), 0);
  };

  const handleBlur = (e) => {
    if (!containerRef.current.contains(e.relatedTarget)) {
      setIsOpen(false);
    }
  };

  const handleAddClick = (e) => {
    onAddOption(searchTerm, e);
    setIsOpen(false);
    setActiveIndex(-1);
    if (onSelect) setTimeout(() => onSelect(searchTerm), 0);
  };

  const filteredOptions = (Array.isArray(options) ? options : []).filter(opt =>
    opt && opt.toString().toLowerCase().includes((searchTerm || '').toLowerCase())
  );

  const showAddOption = creatable && searchTerm && !filteredOptions.some(opt => opt && opt.toString().toLowerCase() === searchTerm.toLowerCase());

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (isOpen) {
        const totalOptions = filteredOptions.length + (showAddOption ? 1 : 0);
        if (activeIndex >= 0 && activeIndex < filteredOptions.length) {
          e.preventDefault();
          const selectedOpt = filteredOptions[activeIndex];
          onChange(selectedOpt);
          setSearchTerm(selectedOpt);
          setIsOpen(false);
          setActiveIndex(-1);
          if (onSelect) setTimeout(() => onSelect(selectedOpt), 0);
          return;
        } else if (showAddOption && activeIndex === filteredOptions.length) {
          e.preventDefault();
          handleAddClick();
          return;
        }
      } else {
        const hasValue = (value && value.trim()) || (searchTerm && searchTerm.trim());
        if (!hasValue && validateOnEnter) {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(true);
          setActiveIndex(0);
          return;
        }
      }
    }

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        setIsOpen(true);
        setActiveIndex(0);
      } else if (e.key === 'ArrowLeft' && onPrevField) {
        // Go to previous field when at start of text
        if (!searchTerm || e.target.selectionStart === 0) {
          e.preventDefault();
          onPrevField();
        }
      }
      return;
    }

    const totalOptions = filteredOptions.length + (showAddOption ? 1 : 0);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => (prev + 1) % (totalOptions || 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => (prev - 1 + totalOptions) % (totalOptions || 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      setIsOpen(false);
      if (onSelect) setTimeout(() => onSelect(searchTerm), 0);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'ArrowLeft' && onPrevField) {
      if (!searchTerm || e.target.selectionStart === 0) {
        e.preventDefault();
        setIsOpen(false);
        onPrevField();
      }
    } else if (e.key === 'Tab' && onTab) {
      e.preventDefault();
      setIsOpen(false);
      onTab();
    }
  };

  return (
    <div className="custom-select-container" ref={containerRef}>
      <input
        ref={ref}
        className="po-input"
        value={searchTerm}
        onChange={handleInputChange}
        onKeyDown={(e) => {
          if (e.key === 'Tab' && onTab) { e.preventDefault(); setIsOpen(false); onTab(); return; }
          handleKeyDown(e);
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={handleBlur}
        placeholder={placeholder || "Select or Type..."}
      />
      {isOpen && (
        <div className="po-dropdown">
          {filteredOptions.map((opt, index) => (
            <div
              key={opt}
              className={`po-option ${index === activeIndex ? 'active' : ''}`}
              onMouseDown={(e) => handleOptionMouseDown(opt, e)}
            >
              {opt}
            </div>
          ))}
          {showAddOption && (
            <div
              className={`po-option ${filteredOptions.length === activeIndex ? 'active' : ''}`}
              style={{ color: '#2563eb', fontWeight: 'bold' }}
              onMouseDown={(e) => {
                e.preventDefault();
                handleAddClick(e);
              }}
            >
              + Add "{searchTerm}"
            </div>
          )}
        </div>
      )}
    </div>
  );
});

const ProductCreatableSelect = React.forwardRef(({ value, onChange, products, category, onAddOption, placeholder, onSelect, onPrevField, onTab, allowAdd = true }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSearchTerm(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    if (isOpen && activeIndex >= 0 && dropdownRef.current) {
      const activeEl = dropdownRef.current.children[activeIndex];
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [activeIndex, isOpen]);

  const handleInputChange = (e) => {
    const newVal = e.target.value;
    setSearchTerm(newVal);
    onChange(newVal);
    setIsOpen(true);
    setActiveIndex(0);
  };

  const handleOptionSelect = (p, fromKeyboard = false) => {
    const val = p === 'Other' ? 'Other' : p.productName;
    const selected = p === 'Other' ? null : p;
    onChange(val, selected);
    setIsOpen(false);
    setActiveIndex(-1);
    // Always fire onSelect (keyboard or mouse) for forward navigation
    if (onSelect) setTimeout(() => onSelect(val, selected), 0);
  };

  const handleBlur = (e) => {
    if (!containerRef.current.contains(e.relatedTarget)) {
      setIsOpen(false);
    }
  };

  const filteredProducts = (products || []).filter(p => {
    const matchesCategory = !category || p.categoryName === category;
    const matchesSearch = (p.productName || '').toLowerCase().includes((searchTerm || '').toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const showAddOption = searchTerm && !filteredProducts.some(p => (p.productName || '').toLowerCase() === searchTerm.toLowerCase());

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const hasValue = (value && value.trim()) || (searchTerm && searchTerm.trim());
      if (!isOpen) {
        if (!hasValue) {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(true);
          setActiveIndex(0);
          return;
        }
      } else {
        const totalOptions = filteredProducts.length + 1;
        if (activeIndex >= 0 && activeIndex < filteredProducts.length) {
          e.preventDefault();
          handleOptionSelect(filteredProducts[activeIndex], true);
          return;
        } else if (allowAdd && activeIndex === totalOptions - 1) {
          e.preventDefault();
          handleOptionSelect('Other', true);
          return;
        } else if (!hasValue) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }
    }

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        setIsOpen(true);
        setActiveIndex(0);
      } else if (e.key === 'ArrowLeft' && onPrevField) {
        if (!searchTerm || e.target.selectionStart === 0) {
          e.preventDefault();
          onPrevField();
        }
      }
      return;
    }

    const totalOptions = filteredProducts.length + (allowAdd ? 1 : 0);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => (prev + 1) % (totalOptions || 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => (prev - 1 + totalOptions) % (totalOptions || 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      setIsOpen(false);
      if (onSelect) setTimeout(() => onSelect(searchTerm), 0);
    } else if (e.key === 'ArrowLeft' && onPrevField) {
      if (!searchTerm || e.target.selectionStart === 0) {
        e.preventDefault();
        setIsOpen(false);
        onPrevField();
      }
    } else if (e.key === 'Tab' && onTab) {
      e.preventDefault();
      setIsOpen(false);
      onTab();
    }
  };

  return (
    <div className="custom-select-container" ref={containerRef}>
      <input
        ref={ref}
        className="po-input"
        style={{ border: 'none', background: 'transparent', width: '100%' }}
        value={searchTerm}
        onChange={handleInputChange}
        onKeyDown={(e) => {
          if (e.key === 'Tab' && onTab) { e.preventDefault(); setIsOpen(false); onTab(); return; }
          handleKeyDown(e);
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={handleBlur}
        placeholder={placeholder || "Select Product..."}
      />
      {isOpen && (filteredProducts.length > 0 || showAddOption || true) && (
        <div className="po-dropdown" ref={dropdownRef}>
          {filteredProducts.map((p, index) => {
            const hsn = p.productHsnCode || p.hsnCode || 'N/A';
            const stock = p.product_stock || p.productStock || p.stock || 0;
            const rate = (parseFloat(p.product_sale_price || p.productSalePrice || 0)).toFixed(2);
            const isSelected = index === activeIndex;

            return (
              <div
                key={p.id || p._id || index}
                className={`po-option ${isSelected ? 'active' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); handleOptionSelect(p, false); }}
              >
                <div className="po-option-name">{p.productName}</div>
                <div className="po-option-details">
                  <span>HSN: {hsn}</span>
                  <span className="product-divider">|</span>
                  <span className={stock > 0 ? 'product-stock-green' : 'product-stock-red'}>
                    Stock: {stock}
                  </span>
                  <span className="product-divider">|</span>
                  <span style={{ color: '#000', fontWeight: 'bold' }}>₹{parseFloat(rate).toFixed(2)}</span>
                </div>
              </div>
            );
          })}
          {allowAdd && (
          <div
            className={`po-option ${activeIndex === filteredProducts.length ? 'active' : ''}`}
            style={{ backgroundColor: activeIndex === filteredProducts.length ? '#f1f5f9' : '#fff', color: '#2563eb', textAlign: 'center', cursor: 'pointer', padding: '10px', fontSize: '13px', borderTop: '1px solid #e2e8f0', fontWeight: 'bold' }}
            onMouseDown={(e) => { e.preventDefault(); handleOptionSelect('Other', false); }}
          >
            + Other (Add New Product)
          </div>
          )}
        </div>
      )}
    </div>
  );
});

const SellerCreatableSelect = React.forwardRef(({ value, onChange, sellers, onAddOption, placeholder, onSelect, onTab }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSearchTerm(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    if (isOpen && activeIndex >= 0 && dropdownRef.current) {
      const activeEl = dropdownRef.current.children[activeIndex];
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [activeIndex, isOpen]);

  const handleInputChange = (e) => {
    const newVal = e.target.value;
    setSearchTerm(newVal);
    onChange(newVal);
    setIsOpen(true);
    setActiveIndex(0);
  };

  const handleOptionSelect = (s, fromKeyboard = false) => {
    const val = s === 'Other' ? 'Other' : (s.sellerTradeName || s.seller_trade_name || s.sellerName || s.seller_name);
    onChange(val, s);
    setSearchTerm(val);
    setIsOpen(false);
    setActiveIndex(-1);
    if (onSelect) setTimeout(() => onSelect(val, s), 0);
  };

  const filteredSellers = (sellers || []).filter(s => {
    const name = (s.sellerTradeName || s.seller_trade_name || s.sellerName || s.seller_name || '').toLowerCase();
    return name.includes((searchTerm || '').toLowerCase());
  });

  const showAddOption = searchTerm && !filteredSellers.some(s => {
    const name = (s.sellerTradeName || s.seller_trade_name || s.sellerName || s.seller_name || '').toLowerCase();
    return name === searchTerm.toLowerCase();
  });

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const hasValue = (value && value.trim()) || (searchTerm && searchTerm.trim());
      if (!isOpen) {
        if (!hasValue) {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(true);
          setActiveIndex(0);
          return;
        }
      } else {
        const totalOptions = filteredSellers.length + 1;
        if (activeIndex >= 0 && activeIndex < filteredSellers.length) {
          e.preventDefault();
          handleOptionSelect(filteredSellers[activeIndex], true);
          return;
        } else if (activeIndex === totalOptions - 1) {
          e.preventDefault();
          handleOptionSelect('Other', true);
          return;
        } else if (!hasValue) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }
    }

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        setIsOpen(true);
        setActiveIndex(0);
      }
      return;
    }

    const totalOptions = filteredSellers.length + 1;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => (prev + 1) % (totalOptions || 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => (prev - 1 + totalOptions) % (totalOptions || 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      setIsOpen(false);
      if (onSelect) setTimeout(() => onSelect(searchTerm), 0);
    } else if (e.key === 'Tab' && onTab) {
      e.preventDefault();
      setIsOpen(false);
      onTab();
    }
  };

  return (
    <div className="custom-select-container" ref={containerRef} style={{ width: '100%', position: 'relative' }}>
      <input
        ref={ref}
        className="po-input"
        style={{ width: '100%' }}
        value={searchTerm}
        onChange={handleInputChange}
        onKeyDown={(e) => {
          if (e.key === 'Tab' && onTab) { e.preventDefault(); setIsOpen(false); onTab(); return; }
          handleKeyDown(e);
        }}
        onFocus={() => setIsOpen(true)}
        placeholder={placeholder || "Select Seller..."}
      />
      {isOpen && (filteredSellers.length > 0 || showAddOption || true) && (
        <div className="po-dropdown" ref={dropdownRef} style={{ width: '400px', maxWidth: '90vw' }}>
          {filteredSellers.map((s, index) => {
            const isSelected = index === activeIndex;
            const name = s.sellerTradeName || s.seller_trade_name || s.sellerName || s.seller_name;
            const gst = s.sellerGstNumber || s.seller_gst_number || 'N/A';
            const state = s.sellerState || s.seller_state || s.sellerCity || s.seller_city || 'N/A';
            
            // Format balance if exists, else default to 0.00 DR
            let balance = parseFloat(s.balance || s.openingBalance || s.opening_balance || 0).toFixed(2);
            let balType = s.balanceType || s.balance_type || 'DR';
            
            return (
              <div
                key={s.id || s._id || index}
                className={`po-option ${isSelected ? 'active' : ''}`}
                style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9' }}
                onMouseDown={(e) => { e.preventDefault(); handleOptionSelect(s, false); }}
              >
                <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '14px', marginBottom: '4px' }}>{name}</div>
                <div style={{ fontSize: '12px', color: '#64748b', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <span>GST: {gst}</span>
                  <span style={{ color: '#cbd5e1' }}>||</span>
                  <span>{state}</span>
                  <span style={{ color: '#cbd5e1' }}>||</span>
                  <span style={{ color: '#3b82f6', fontWeight: '500' }}>Bal: {balance} {balType}</span>
                </div>
              </div>
            );
          })}
          <div
            className={`po-option ${activeIndex === filteredSellers.length ? 'active' : ''}`}
            style={{ backgroundColor: activeIndex === filteredSellers.length ? '#f1f5f9' : '#fff', color: '#2563eb', textAlign: 'center', cursor: 'pointer', padding: '10px', fontSize: '13px', fontWeight: 'bold' }}
            onMouseDown={(e) => { e.preventDefault(); handleOptionSelect('Other', false); }}
          >
            + Add New Party "{searchTerm}"
          </div>
        </div>
      )}
    </div>
  );
});

function AddPurchaseOrderForm({ isOpen, onClose, editingRow, isPage, isEdit: isEditProp }) {
  const { hasPermission } = usePermissionContext();
  const isEdit = isEditProp !== undefined ? isEditProp : !!editingRow;

  const [general, setGeneral] = useState({
    seller: '',
    invoiceNo: '',
    invoiceDate: new Date().toISOString().slice(0, 10),
    vehicleNo: '',
    lrNo: '',
    eWayBillNo: '',
    discountPercent: '0',
    freightPercent: '0',
    insurancePercent: '0',
    transporterName: '',
    preparationDate: new Date().toISOString().slice(0, 10),
    preparationTime: new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
    removalDate: '',
    removalTime: '',
    remarks: '',
    termsAndConditions: '',
    paymentType: 'Credit',
    invoiceName: '',  // will be set to first invoice name after fetch
    saleType: '', // Internal use
    extraChargesAmount: '0',
    lrDate: '',
    containerNo: '',
    seal_no: '',
    transporter_id: ''
  });

  const [productRows, setProductRows] = useState([
    {
      id: Date.now(),
      category: '',
      product: '',
      hsnCode: '',
      grade: '',
      unit: 'PCS',
      color: '',
      quantity: '1',
      rate: '',
      total: '',
      showNewProduct: false,
      newProduct: {
        name: '',
        hsn: '',
        modelNumber: '',
        finishGlaze: '',
        salePrice: '',
        type: 'Stock'
      }
    },
  ]);

  // Lists from DB
  const [sellers, setSellers] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [colorList, setColorList] = useState([]);
  const [gradeList, setGradeList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [ownCompany, setOwnCompany] = useState(null);
  const [allCountries, setAllCountries] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [saleTypes, setSaleTypes] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [transporters, setTransporters] = useState([]);
  const [invoiceNamesList, setInvoiceNamesList] = useState([]);
  const [showAddSellerModal, setShowAddSellerModal] = useState(false);

  // Integrated Inline OCR states
  const [ocrFile, setOcrFile] = useState(null);
  const [ocrFileName, setOcrFileName] = useState('');
  const [ocrStatus, setOcrStatus] = useState('NOT_LOADED'); // 'NOT_LOADED', 'LOADING', 'COMPLETED', 'FAILED'
  const [ocrError, setOcrError] = useState('');
  const fileInputRef = useRef(null);

  const handleOcrFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are supported.');
      return;
    }

    setOcrFile(file);
    setOcrFileName(file.name);
    setOcrStatus('LOADING');
    setOcrError('');

    try {
      const token = sessionStorage.getItem('token');
      const formData = new FormData();
      formData.append('invoiceFile', file);

      const response = await fetch(`${API_BASE_URL}/invoice-parser/parse`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const json = await response.json();

      if (!response.ok) {
        throw new Error(json.message || `Server error ${response.status}`);
      }

      const data = json.data;
      handleOCRDataExtracted(data);
      setOcrStatus('COMPLETED');
      toast.success('Invoice processed and form auto-filled successfully!');
    } catch (err) {
      console.error('[Integrated OCR] Error:', err);
      const errMsg = err.message || 'Failed to parse invoice. Please try again.';
      setOcrError(errMsg);
      setOcrStatus('FAILED');
      toast.error(errMsg);
    }
  };

  useEffect(() => {
    fetchMasterData();
    const fetchTransporters = async () => {
      try {
        const result = await fetchApi('/transporter?limit=1000');
        setTransporters(result.transporters || []);
      } catch (err) { console.error(err); }
    };
    fetchTransporters();
  }, []);
  useEffect(() => {
    if (editingRow) {
      const isFromChallan = !isEdit && !!editingRow;
      // Build initial general state from editingRow with robust fallbacks
      const initialGeneral = {
        seller: editingRow.seller || editingRow.companyName || editingRow.company_name || '',
        invoiceNo: isFromChallan ? (editingRow.challan_no || editingRow.challanNo || '') : (editingRow.invoiceNo || editingRow.invoice_no || ''),
        invoiceDate: isFromChallan ? new Date().toISOString().slice(0, 10) : (editingRow.invoiceDate || editingRow.invoice_date || ''),
        vehicleNo: editingRow.vehicleNo || editingRow.vehicle_no || '',
        lrNo: editingRow.lrNo || editingRow.lr_no || '',
        lrDate: editingRow.lrDate || editingRow.lr_date || '',
        containerNo: editingRow.containerNo || editingRow.container_no || '',
        seal_no: editingRow.seal_no || '',
        eWayBillNo: editingRow.eWayBillNo || editingRow.e_way_bill_no || '',
        transporterName: editingRow.transporterName || editingRow.transporter_name || '',
        transporter_id: editingRow.transporter_id || '',
        preparationDate: isFromChallan ? new Date().toISOString().slice(0, 10) : (
          (() => {
            const d = editingRow.preparationDate || editingRow.prerartionDate || editingRow.prerartion_date;
            if (!d || d === 'null' || d === 'undefined' || d === '0000-00-00') return new Date().toISOString().slice(0, 10);
            return d;
          })()
        ),
        preparationTime: isFromChallan ? new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : (
          (() => {
            const t = editingRow.preparationTime || editingRow.prerartionTime || editingRow.prerartion_time;
            if (!t || t === 'null' || t === 'undefined' || t === '00:00:00' || t === '00:00' || t === 'undefined:undefined') {
              return new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
            }
            return t;
          })()
        ),
        removalDate: editingRow.removalDate || '',
        removalTime: editingRow.removalTime || '',
        freightPercent: editingRow.freight || '0',
        discountPercent: editingRow.discount || '0',
        insurancePercent: editingRow.insurance || '0',
        remarks: editingRow.remarks || '',
        termsAndConditions: editingRow.termsAndConditions || editingRow.termsCondtion || editingRow.terms_condtion || '',
        paymentType: editingRow.paymentType || editingRow.invoice_payment_type_name || 'Credit',
        invoiceName: isFromChallan
          ? (editingRow.challan_name || editingRow.challanName || editingRow.invoiceName || editingRow.invoice_name || '')
          : (editingRow.invoiceName || editingRow.invoice_name || ''),
        saleType: editingRow.saleType || editingRow.sale_type_name || editingRow.saleTypeName || editingRow.sale_type || '',
        extraChargesAmount: editingRow.extraChargesAmount || editingRow.extr_charges_amount || '0'
      };
      setGeneral(initialGeneral);
      if (editingRow.items) {
        let itemsArray = [];
        try {
          itemsArray = typeof editingRow.items === 'string' ? JSON.parse(editingRow.items) : editingRow.items;
        } catch (e) {
          console.error("Error parsing items:", e);
          itemsArray = [];
        }
        if (Array.isArray(itemsArray) && itemsArray.length) {
          setProductRows(itemsArray.map((it, idx) => ({ 
            ...it, 
            id: it._id || it.id || (Date.now() + idx),
            sourceChallanId: it.sourceChallanId || (isFromChallan ? (editingRow._id || editingRow.id) : null)
          })));
        }
      }
    } else {
      setGeneral(prev => ({
        ...prev,
        invoiceDate: new Date().toISOString().slice(0, 10),
      }));
      setProductRows([
        {
          id: Date.now(),
          category: '',
          product: '',
          hsnCode: '',
          grade: '',
          unit: 'PCS',
          color: '',
          quantity: '1',
          rate: '',
          total: '',
          showNewProduct: false,
          newProduct: {}
        },
      ]);
    }
  }, [isEdit, editingRow]);

  const fetchMasterData = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [
        sellRes, catRes, colRes, gradRes, prodRes, compRes, countryRes, invTypRes, saleTypRes
      ] = await Promise.all([
        fetch(`${API_BASE_URL}/seller`, { headers }),
        fetch(`${API_BASE_URL}/category`, { headers }),
        fetch(`${API_BASE_URL}/color`, { headers }),
        fetch(`${API_BASE_URL}/grade`, { headers }),
        fetch(`${API_BASE_URL}/product`, { headers }),
        fetch(`${API_BASE_URL}/company`, { headers }),
        fetch(`${API_BASE_URL}/country`, { headers }),
        fetch(`${API_BASE_URL}/invoice-name`, { headers }),
        fetch(`${API_BASE_URL}/sale-type?limit=0`, { headers })
      ]);

      if (sellRes.ok) {
        const result = await sellRes.json();
        setSellers(result.sellers || (Array.isArray(result) ? result : []));
      }
      if (catRes.ok) {
        const result = await catRes.json();
        const cats = result.categories || (Array.isArray(result) ? result : []);
        setCategoryList(cats.map(c => c.categoryName));
      }
      if (colRes.ok) {
        const result = await colRes.json();
        const cols = result.colors || (Array.isArray(result) ? result : []);
        setColorList(cols.map(c => c.colorName));
      }
      if (gradRes.ok) {
        const result = await gradRes.json();
        const grads = result.grades || (Array.isArray(result) ? result : []);
        setGradeList(grads.map(g => g.gradeName));
      }
      if (prodRes.ok) {
        const result = await prodRes.json();
        setProductList(result.products || (Array.isArray(result) ? result : []));
      }
      if (compRes.ok) {
        const result = await compRes.json();
        const companies = result.companies || (Array.isArray(result) ? result : []);
        setOwnCompany(companies[0] || null);
      }
      if (countryRes.ok) {
        const countries = await countryRes.json();
        setAllCountries(countries);
        setCountryList(countries.map(c => c.country_name || c.countryName));
      }
      if (invTypRes.ok) {
        const result = await invTypRes.json();
        // /invoice-name returns a plain array when no pagination params are given
        const names = Array.isArray(result)
          ? result
          : (result.invoiceNames || []);
        const nameStrings = names.map(n => n.invoiceShortName || n.invoice_short_name || n.name).filter(Boolean);
        setInvoiceNamesList(nameStrings);
        // Auto-set the first invoice name as default if not editing
        if (nameStrings.length > 0) {
          setGeneral(prev => ({
            ...prev,
            invoiceName: prev.invoiceName || nameStrings[0]
          }));
        }
      }
      if (saleTypRes.ok) {
        const result = await saleTypRes.json();
        setSaleTypes(result.saleTypes || (Array.isArray(result) ? result : []));
      }

    } catch (error) {
      console.error("Error fetching master data:", error);
    }
  };

  const updateGeneral = (field, value) => {
    setGeneral((prev) => ({ ...prev, [field]: value }));
  };

  const handleOCRDataExtracted = (data) => {
    if (!data) return;

    // 1. Find matched seller object if any
    let matchedSeller = null;
    if (data.sellerId) {
      matchedSeller = sellers.find(
        (s) => String(s.id || s._id) === String(data.sellerId)
      );
    }

    // 2. Determine Sale Type (Local vs Interstate)
    const sellerGst = data.sellerGstNumber || matchedSeller?.sellerGstNumber || matchedSeller?.seller_gst_number || '';
    const buyerGst = ownCompany?.companyGstNumber || '';
    
    let isInterstate = false;
    if (sellerGst && buyerGst) {
      const sellerState = sellerGst.substring(0, 2);
      const buyerState = buyerGst.substring(0, 2);
      if (sellerState !== buyerState) {
        isInterstate = true;
      }
    }

    // Determine the most common tax percentage from the parsed items
    let detectedTaxPercent = 18; // Default fallback in case not found
    if (Array.isArray(data.items) && data.items.length > 0) {
      const itemWithGst = data.items.find(item => item.gstPercent && !isNaN(parseFloat(item.gstPercent)));
      if (itemWithGst) {
        detectedTaxPercent = parseFloat(itemWithGst.gstPercent);
      }
    }

    // Find the matching saleType from the list of fetched saleTypes
    let matchedSaleType = '';
    if (saleTypes && saleTypes.length > 0) {
      const filtered = saleTypes.filter(s => {
        const name = (s.saleTypeName || s.sale_type_name || '').toLowerCase();
        if (isInterstate) {
          return name.includes('interstate') || name.includes('igst') || name.includes('out');
        } else {
          return !name.includes('interstate') && !name.includes('igst') && !name.includes('out') && (name.includes('local') || name.includes('cgst'));
        }
      });

      let bestMatch = filtered.find(s => {
        const name = (s.saleTypeName || s.sale_type_name || '').toLowerCase();
        return name.includes(String(detectedTaxPercent));
      });

      if (!bestMatch && filtered.length > 0) {
        bestMatch = filtered[0];
      }

      if (bestMatch) {
        matchedSaleType = bestMatch.saleTypeName || bestMatch.sale_type_name;
      }
    }

    // ── Auto-fill general header fields ─────────────────────────────────
    setGeneral((prev) => ({
      ...prev,
      seller:           matchedSeller ? (matchedSeller.sellerTradeName || matchedSeller.seller_trade_name || data.seller) : (data.seller || prev.seller),
      invoiceNo:        data.invoiceNo        || prev.invoiceNo,
      invoiceDate:      data.invoiceDate      || prev.invoiceDate,
      transporterName:  data.transporterName  || prev.transporterName,
      vehicleNo:        data.vehicleNo        || prev.vehicleNo,
      saleType:         matchedSaleType       || prev.saleType
    }));

    // ── Auto-fill product rows ───────────────────────────────────────────
    if (Array.isArray(data.items) && data.items.length > 0) {
      const newRows = data.items.map((item, idx) => ({
        id: Date.now() + idx,
        category:   item.category   || '',
        product:    item.product    || '',
        productId:  item.productId  || null,
        hsnCode:    item.hsnCode    || '',
        grade:      item.grade      || '',
        unit:       item.unit       || 'PCS',
        color:      item.color      || '',
        quantity:   String(item.quantity || '1'),
        rate:       String(item.rate     || ''),
        total:      String(item.total    || ''),
        gstPercent: item.gstPercent || '',
        // For unmatched products: show the new-product panel pre-filled
        showNewProduct: !!item.showNewProduct,
        newProduct: item.newProduct || {},
      }));
      setProductRows(newRows);
    }
  };




  const updateProductRow = (id, field, value) => {
    setProductRows((prev) =>
      prev.map((r) => {
        if (r.id === id) {
          const updated = { ...r, [field]: value };

          // Reset product if category changes
          if (field === 'category') {
            updated.product = '';
            updated.hsnCode = '';
            updated.showNewProduct = false;
          }

          // Handle 'Product' dropdown 'Other' selection
          if (field === 'product') {
            if (value === 'Other') {
              updated.showNewProduct = true;
              if (!updated.newProduct) {
                updated.newProduct = {
                  name: '',
                  hsn: '',
                  modelNumber: '',
                  finishGlaze: '',
                  salePrice: '',
                  type: 'Stock'
                };
              }
              // Focus the new name input
              setTimeout(() => {
                rowRefs.current[id]?.newName?.focus();
              }, 150);
            } else {
              updated.showNewProduct = false;
              // Auto-fill HSN and Rate from productList if available
              const selected = productList.find(p => p.productName === value);
              if (selected) {
                updated.productId = selected.id || selected._id;
                updated.product_id = selected.id || selected._id;
                updated.hsnCode = selected.productHsnCode || '';
                updated.rate = selected.productSalePrice || '';
                updated.productSalePrice = selected.productSalePrice || '';
                const qty = Number(updated.quantity) || 0;
                const rate = Number(updated.rate) || 0;
                updated.total = (qty * rate).toFixed(2);
              }
            }
          }

          if (field === 'quantity' || field === 'rate') {
            const qty = Number(updated.quantity) || 0;
            const rate = Number(updated.rate) || 0;
            updated.total = (qty * rate).toFixed(2);
          }
          return updated;
        }
        return r;
      })
    );
  };

  const updateProductMultipleFields = (id, fields) => {
    setProductRows((prev) =>
      prev.map((r) => {
        if (r.id === id) {
          const updated = { ...r, ...fields };
          const qty = Number(updated.quantity) || 0;
          const rate = Number(updated.rate) || 0;
          updated.total = (qty * rate).toFixed(2);
          return updated;
        }
        return r;
      })
    );
  };

  const updateNewProductField = (id, field, value) => {
    setProductRows((prev) =>
      prev.map((r) => {
        if (r.id === id) {
          return {
            ...r,
            newProduct: {
              ...r.newProduct || {},
              [field]: value
            }
          };
        }
        return r;
      })
    );
  };

  const handleAddCategory = async (newCategory) => {
    if (!newCategory) return;
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/category`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ categoryName: newCategory })
      });

      if (response.ok) {
        setCategoryList(prev => [...prev, newCategory]);
        toast.success(`Category "${newCategory}" Added Successfully!`);
      } else {
        const err = await response.json();
        toast.error(err.message || "Failed to add category to database");
      }
    } catch (error) {
      console.error("Add Category Error:", error);
      toast.error("Server error while adding category");
    }
  };

  const handleAddColor = async (newColor) => {
    if (!newColor) return;
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/color`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ colorName: newColor })
      });

      if (response.ok) {
        setColorList(prev => [...prev, newColor]);
        toast.success(`Color "${newColor}" Added Successfully!`);
      } else {
        const err = await response.json();
        toast.error(err.message || "Failed to add color to database");
      }
    } catch (error) {
      console.error("Add Color Error:", error);
      toast.error("Server error while adding color");
    }
  };

  const addProductRow = () => {
    const newId = Date.now();
    setProductRows((prev) => [
      ...prev,
      {
        id: newId,
        category: '',
        product: '',
        hsnCode: '',
        grade: '',
        unit: 'PCS',
        color: '',
        quantity: '1',
        rate: '',
        total: '',
        showNewProduct: false,
        newProduct: {
          name: '',
          hsn: '',
          modelNumber: '',
          finishGlaze: '',
          salePrice: '',
          type: 'Stock'
        }
      },
    ]);
    return newId;
  };

  const removeProductRow = (id) => {
    if (productRows.length > 1) {
      setProductRows((prev) => prev.filter((r) => r.id !== id));
    } else {
      toast.warn("At least one product item is required.");
    }
  };

  const handleSubmit = async (e) => {
    if(e) e.preventDefault();
    const conf = await Swal.fire({
      title: 'Save Data?',
      text: 'Are you sure you want to save this data?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#00334e',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Save',
      cancelButtonText: 'No, Keep Editing'
    });
    if(!conf.isConfirmed) return;

    // Mandatory Field Validation
    if (!general.seller) {
      toast.error("Seller is mandatory");
      return;
    }
    if (!general.invoiceNo) {
      toast.error("Invoice No is mandatory");
      return;
    }

    // Filter out completely blank rows
    const activeProductRows = productRows.filter(row => row.category || row.product || (row.rate && row.rate !== ''));

    if (activeProductRows.length === 0) {
      toast.error("At least one product item is required.");
      return;
    }

    for (let i = 0; i < activeProductRows.length; i++) {
      const row = activeProductRows[i];
      if (!row.category) {
        toast.error(`Category is mandatory for Item ${i + 1}`);
        return;
      }
      if (!row.product) {
        toast.error(`Product is mandatory for Item ${i + 1}`);
        return;
      }
      if (row.product === 'Other' && (!row.newProduct?.name)) {
        toast.error(`Please enter New Product Name for Item ${i + 1}`);
        return;
      }
      if (!row.rate) {
        toast.error(`Rate is mandatory for Item ${i + 1}`);
        return;
      }
      
      // Permission Validations for New Category / Product
      if (row.category && !categoryList.some(c => c.toLowerCase() === row.category.toLowerCase())) {
        if (!hasPermission('Category', 'add')) {
          toast.error(`You do not have permission to add a new Category ("${row.category}"). Please select an existing category.`);
          return;
        }
      }
      if (row.product === 'Other' || (row.product && !productList.some(p => (p.productName || '').toLowerCase() === row.product.toLowerCase()))) {
        if (!hasPermission('Product', 'add')) {
          const prodName = row.product === 'Other' ? row.newProduct?.name : row.product;
          toast.error(`You do not have permission to add a new Product ("${prodName}"). Please select an existing product.`);
          return;
        }
      }
    }

    let selectedSellerObj = sellers.find(s => s.sellerTradeName === general.seller);
    const token = sessionStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };

    if (!selectedSellerObj && general.seller !== 'Other') {
      toast.error("Seller is require to add");
      return;
    }

    // If 'Other' is selected, you must use the modal to add a seller
    if (general.seller === 'Other') {
      toast.error("Please add new seller details using the modal or select an existing seller");
      return;
    }

    const selectedSaleType = saleTypes.find(s =>
      (s.saleTypeName || s.sale_type_name) === general.saleType
    );
    const subTotal = activeProductRows.reduce((sum, it) => sum + (parseFloat(it.total) || 0), 0);
    const discountAmt = (subTotal * (parseFloat(general.discountPercent) || 0)) / 100;
    const extraAmt = parseFloat(general.extraChargesAmount) || 0;
    const taxableVal = subTotal - discountAmt + extraAmt;
    const tax1 = parseFloat(selectedSaleType?.saleTypeTax1 || selectedSaleType?.sale_type_tax1) || 0;
    const tax2 = parseFloat(selectedSaleType?.saleTypeTax2 || selectedSaleType?.sale_type_tax2) || 0;
    const taxAmount = (taxableVal * (tax1 + tax2)) / 100;
    const insuranceAmt = (subTotal * (parseFloat(general.insurancePercent) || 0)) / 100;
    const freightAmt = (subTotal * (parseFloat(general.freightPercent) || 0)) / 100;
    const grandTotal = taxableVal + taxAmount + insuranceAmt + freightAmt;

    const payload = {
      // Buyer Details (This Company)
      buyerId: ownCompany?.id,
      buyerTradeName: ownCompany?.companyName,
      buyerMobileNumber: ownCompany?.companyMobileNumber_1,
      buyerEmail: ownCompany?.companyEmail,
      buyerPanCardNumber: ownCompany?.companyPanCardNumber,
      buyerGstNumber: ownCompany?.companyGstNumber,
      buyerCinNumber: ownCompany?.companyCinNumber,
      buyerCountry: ownCompany?.companyCountry,
      buyerState: ownCompany?.companyState,
      buyerCity: ownCompany?.companyCity,
      buyerPinCode: ownCompany?.companyPinCode,
      buyerAddress: ownCompany?.companyAddress,
      buyerBankName: ownCompany?.companyBankName,
      buyerBankAccountName: ownCompany?.companyBankAccountName,
      buyerAccountNo: ownCompany?.companyBankAccountNumber,
      buyerIfscCode: ownCompany?.companyBankIfscCode,

      // Seller Details
      companyName: selectedSellerObj?.sellerTradeName || general.seller,
      companyMobileNumber: selectedSellerObj?.sellerMobileNumber,
      companyEmail: selectedSellerObj?.sellerEmail,
      companyGstNumber: selectedSellerObj?.sellerGstNumber,
      companyPanCardNumber: selectedSellerObj?.sellerPanCardNumber,
      companyCinNumber: selectedSellerObj?.sellerCinNumber,
      companyCountry: selectedSellerObj?.sellerCountry || editingRow?.companyCountry,
      companyState: selectedSellerObj?.sellerState || editingRow?.companyState,
      companyCity: selectedSellerObj?.sellerCity || editingRow?.companyCity,
      companyPinCode: selectedSellerObj?.sellerPinCode || editingRow?.companyPinCode,
      companyAddress: selectedSellerObj?.sellerAddress || editingRow?.companyAddress,
      purchaseCompanyId: selectedSellerObj?.id || selectedSellerObj?._id || selectedSellerObj?.sellerId, // Fix: Use correct ID field

      invoiceNo: general.invoiceNo,
      invoiceDate: general.invoiceDate,
      prerartionDate: general.preparationDate,
      prerartionTime: general.preparationTime,
      preparationDate: general.preparationDate,
      preparationTime: general.preparationTime,
      prerartion_date: general.preparationDate,
      prerartion_time: general.preparationTime,
      removalDate: general.removalDate,
      removalTime: general.removalTime,
      transporterName: general.transporterName,
      vehicleNo: general.vehicleNo,
      lrNo: general.lrNo,
      eWayBillNo: general.eWayBillNo,
      freight: general.freightPercent,
      discount: parseFloat(general.discountPercent) || 0,
      discount_type: '%',
      insurance: general.insurancePercent,
      remarks: general.remarks,
      termsCondtion: general.termsAndConditions,

      // New Miracle Fields
      paymentType: general.paymentType,
      invoiceName: general.invoiceName,
      saleType: general.saleType,
      extraChargesAmount: general.extraChargesAmount,
      sale_type_tax1: tax1,
      sale_type_tax2: tax2,

      items: activeProductRows.map(r => {
        const isNewProduct = !!(r.newProduct?.name);
        return {
          category: r.category,
          product: r.product,
          product_id: r.productId || r.product_id,
          hsnCode: isNewProduct ? (r.newProduct?.hsn || r.hsnCode) : r.hsnCode,
          modelNumber: isNewProduct ? (r.newProduct?.modelNumber || '') : (r.modelNumber || ''),
          productFinishGlaze: isNewProduct ? (r.newProduct?.finishGlaze || '') : (r.productFinishGlaze || ''),
          productSalePrice: isNewProduct ? (r.newProduct?.salePrice || '') : (r.productSalePrice || ''),
          grade: r.grade,
          unit: r.unit,
          color: r.color,
          quantity: Number(r.quantity),
          rate: r.rate,
          total: r.total,
          sourceChallanId: r.sourceChallanId // Pass source challan ID if available
        };
      }),
      totalAmount: grandTotal,
      isFromChallan: !isEdit && editingRow ? true : false,
      challanId: !isEdit && editingRow ? (editingRow._id || editingRow.id) : null,
      sourceChallanIds: !isEdit && editingRow?.sourceChallanIds ? editingRow.sourceChallanIds : null
    };

    try {
      const url = isEdit ? `/purchase-order/${editingRow._id || editingRow.id}` : `/purchase-order`;

          const response = await fetchApi(url, {
            method: isEdit ? 'PUT' : 'POST',
            body: JSON.stringify(payload)
          });

          if (response && response.Status !== false) {
            toast.success(isEdit ? 'Purchase Order Updated!' : 'Purchase Order Created!');

            onClose();
          } else {
            toast.error(response?.message || response?.Error || "Operation failed");
          }
        } catch (error) {
          console.error("Submit Error:", error);
          toast.error("Server error");
        }
  };

  const selectedSaleTypeInfo = saleTypes.find(s =>
    (s.saleTypeName || s.sale_type_name) === general.saleType
  );
  const tax1Rate = parseFloat(selectedSaleTypeInfo?.saleTypeTax1 || selectedSaleTypeInfo?.sale_type_tax1) || 0;
  const tax2Rate = parseFloat(selectedSaleTypeInfo?.saleTypeTax2 || selectedSaleTypeInfo?.sale_type_tax2) || 0;

  const subTotalCalc = productRows.reduce((sum, it) => sum + (parseFloat(it.total) || 0), 0);
  const totalQtyCalc = productRows.reduce((sum, it) => sum + (parseFloat(it.quantity) || 0), 0);
  const discountPercentageCalc = parseFloat(general.discountPercent) || 0;
  const discountAmountCalc = (subTotalCalc * discountPercentageCalc) / 100;
  const extraChargesAmountCalc = parseFloat(general.extraChargesAmount) || 0;
  const taxableValCalc = subTotalCalc - discountAmountCalc + extraChargesAmountCalc;

  const insurancePercentageCalc = parseFloat(general.insurancePercent) || 0;
  const insuranceAmountCalc = (subTotalCalc * insurancePercentageCalc) / 100;
  const freightPercentageCalc = parseFloat(general.freightPercent) || 0;
  const freightAmountCalc = (subTotalCalc * freightPercentageCalc) / 100;
  const tax1AmountCalc = (taxableValCalc * tax1Rate) / 100;
  const tax2AmountCalc = (taxableValCalc * tax2Rate) / 100;
  const grandTotalCalc = taxableValCalc + tax1AmountCalc + tax2AmountCalc + insuranceAmountCalc + freightAmountCalc;

  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const billDay = general.invoiceDate ? dayNames[new Date(general.invoiceDate).getDay()] : "";

  const firstInputRef = useRef(null);

  // ── Keyboard navigation refs ──────────────────────────────────────────────
  const sellerRef = useRef(null);
  const invoiceNameRef = useRef(null);
  const billNoRef = useRef(null);
  const vehicleNoRef = useRef(null);
  const lrNoRef = useRef(null);
  const lrDateRef = useRef(null);
  const eWayBillRef = useRef(null);
  // Per-row refs: rowRefs[rowIndex][colName]
  const rowRefs = useRef({});
  const saleTypeRef = useRef(null);
  const termsRef = useRef(null);
  const remarksRef = useRef(null);
  const insuranceRef = useRef(null);
  const discountRef = useRef(null);
  const freightRef = useRef(null);
  const extraChargesRef = useRef(null);
  const saveBtnRef = useRef(null);

  // Ensure rowRefs array grows with productRows
  const ensureRowRef = (id) => {
    if (!rowRefs.current[id]) rowRefs.current[id] = {};
    return rowRefs.current[id];
  };

  const focusRowCol = (rowId, col) => {
    setTimeout(() => {
      const ref = rowRefs.current[rowId]?.[col];
      if (ref) ref.focus();
    }, 80);
  };

  // Always focus seller on mount (page mode)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (sellerRef.current) {
        sellerRef.current.focus();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Also focus when modal opens/reopens
  useEffect(() => {
    if (isOpen && !isPage) {
      setTimeout(() => sellerRef.current?.focus(), 150);
    }
  }, [isOpen]);

  if (!isOpen && !isPage) return null;

  const formContent = (
    <div className={isPage ? "page-card" : "modal"} onClick={(e) => !isPage && e.stopPropagation()} style={isPage ? { maxWidth: '100%', margin: '0' } : {}}>
      {!isPage && (
        <div className="po-header">
          <h2 className="po-title">{isEdit ? 'Update Purchase Invoice' : 'Add Purchase Invoice'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button type="button" className="po-close" onClick={onClose}>✕</button>
          </div>
        </div>
      )}
      {isPage && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px', padding: '15px' }}>
          <div className="page-card__title" style={{ textAlign: 'center', fontWeight: '500', fontSize: '24px' }}>{isEdit ? 'Update Purchase Invoice' : 'Add Purchase Invoice'}</div>
        </div>
      )}

      {/* Integrated Inline OCR Upload Section */}
      {!isEdit && (
        <div style={{ padding: '0 20px' }}>
          <div className="card shadow-sm border-0 mb-3" style={{ borderRadius: '12px', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <div className="card-body p-3">
              {/* Header Row */}
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                <div className="d-flex align-items-center gap-2">
                  <span className="fw-semibold text-slate-800" style={{ fontSize: '15px', color: '#1e293b' }}>Upload Invoice Document</span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf"
                    style={{ display: 'none' }}
                    onChange={handleOcrFileChange}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-white border px-3"
                    style={{ borderRadius: '6px', fontSize: '12px', fontWeight: '600', color: '#475569', background: '#ffffff', borderColor: '#cbd5e1' }}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={ocrStatus === 'LOADING'}
                  >
                    Add File
                  </button>
                </div>
                
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted" style={{ fontSize: '12px' }}>
                    {ocrStatus === 'NOT_LOADED' && 'No document selected'}
                    {ocrStatus === 'LOADING' && `Selected: ${ocrFileName}`}
                    {ocrStatus === 'COMPLETED' && 'Queued: 1 | Completed: 1 | Failed: 0 | Waiting: 0'}
                    {ocrStatus === 'FAILED' && 'Queued: 1 | Completed: 0 | Failed: 1 | Waiting: 0'}
                  </span>
                  
                  {ocrStatus === 'NOT_LOADED' && (
                    <span className="badge px-2 py-1 fw-semibold text-secondary" style={{ background: '#e2e8f0', color: '#475569', fontSize: '11px', borderRadius: '20px' }}>Not Loaded</span>
                  )}
                  {ocrStatus === 'LOADING' && (
                    <span className="badge px-2 py-1 fw-semibold text-primary" style={{ background: '#eff6ff', color: '#2563eb', fontSize: '11px', borderRadius: '20px' }}>Loading...</span>
                  )}
                  {ocrStatus === 'COMPLETED' && (
                    <span className="badge px-2 py-1 fw-semibold text-success" style={{ background: '#f0fdf4', color: '#16a34a', fontSize: '11px', borderRadius: '20px' }}>Completed</span>
                  )}
                  {ocrStatus === 'FAILED' && (
                    <span className="badge px-2 py-1 fw-semibold text-danger" style={{ background: '#fef2f2', color: '#dc2626', fontSize: '11px', borderRadius: '20px' }}>Failed</span>
                  )}
                </div>
              </div>
              
              {/* Subtitle / Processing status */}
              <div className="mb-2" style={{ fontSize: '13px' }}>
                {ocrStatus === 'NOT_LOADED' && <span className="text-secondary">Choose a document to auto-fill the invoice.</span>}
                {ocrStatus === 'LOADING' && <span className="text-primary fw-semibold">Processing 1 document. Completed: 0 | Failed: 0 | Waiting: 0</span>}
                {ocrStatus === 'COMPLETED' && <span className="text-success fw-semibold">Processed 1 document successfully.</span>}
                {ocrStatus === 'FAILED' && <span className="text-danger fw-semibold">Failed to process document.</span>}
              </div>

              {/* List Item Box / Dashed Box */}
              {ocrStatus === 'NOT_LOADED' ? (
                <div className="d-flex align-items-center justify-content-center p-3 text-center border-dashed" style={{ border: '1px dashed #cbd5e1', borderRadius: '8px', background: '#ffffff', minHeight: '50px' }}>
                  <span className="text-muted" style={{ fontSize: '13px' }}>Each selected document will appear here with its processing status.</span>
                </div>
              ) : (
                <div className="d-flex align-items-center justify-content-between p-2 border bg-white shadow-sm" style={{ borderRadius: '8px', borderColor: '#e2e8f0' }}>
                  <div className="d-flex align-items-center gap-3">
                    <span className="fs-4" style={{ lineHeight: 1 }}>📄</span>
                    <div>
                      <div className="fw-semibold text-dark" style={{ fontSize: '14px' }}>{ocrFileName}</div>
                      <div className="text-muted" style={{ fontSize: '12px' }}>
                        {ocrStatus === 'LOADING' && 'Reading document and filling invoice...'}
                        {ocrStatus === 'COMPLETED' && 'Invoice data loaded from document.'}
                        {ocrStatus === 'FAILED' && `Error: ${ocrError}`}
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    {ocrStatus === 'LOADING' && (
                      <span className="badge px-2 py-1 text-primary bg-primary-light" style={{ background: '#eff6ff', color: '#2563eb', fontSize: '11px', borderRadius: '6px', fontWeight: 'bold' }}>LOADING</span>
                    )}
                    {ocrStatus === 'COMPLETED' && (
                      <span className="badge px-2 py-1 text-success bg-success-light" style={{ background: '#f0fdf4', color: '#16a34a', fontSize: '11px', borderRadius: '6px', fontWeight: 'bold' }}>COMPLETED</span>
                    )}
                    {ocrStatus === 'FAILED' && (
                      <span className="badge px-2 py-1 text-danger bg-danger-light" style={{ background: '#fef2f2', color: '#dc2626', fontSize: '11px', borderRadius: '6px', fontWeight: 'bold' }}>FAILED</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="po-body">
        <form className="po-form" onSubmit={handleSubmit}>

          {/* MIRACLE EXACT DESIGN TOP SECTION */}
          <div className="miracle-exact-top">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px 70px' }}>

              {/* Row 1, Col 1: Party A/c. */}
              <div className="miracle-col-compact">
                <div className="miracle-row" style={{ gap: '6px' }}>
                  <label style={{ width: '75px' }}>Party A/c.</label>
                  <div style={{ display: 'flex', gap: '6px', flex: 1, alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <SellerCreatableSelect
                        ref={sellerRef}
                        value={general.seller}
                        sellers={sellers}
                        onChange={(val) => {
                          if (val === 'Other') {
                            setShowAddSellerModal(true);
                          } else {
                            updateGeneral('seller', val);
                          }
                        }}
                        onAddOption={(val) => {
                          setShowAddSellerModal(true);
                        }}
                        onSelect={() => billNoRef.current?.focus()}
                        placeholder="Select Seller"
                      />
                    </div>
                    <button type="button" className="btn-add-miracle" onClick={() => setShowAddSellerModal(true)}>Add New</button>
                  </div>
                </div>
              </div>

              {/* Row 1, Col 2: Bill Date */}
              <div className="miracle-col-compact">
                <div className="miracle-row" style={{ gap: '6px' }}>
                  <label style={{ width: '60px' }}>Bill Date</label>
                  <div className="miracle-row" style={{ width: '100%', gap: '0' }}>
                    <AirDatePicker
                      className="po-input"
                      value={general.invoiceDate}
                      onChange={(val) => updateGeneral('invoiceDate', val)}
                    />
                    <div className="day-label">{billDay}</div>
                  </div>
                </div>
              </div>

              {/* Row 1, Col 3: Time */}
              <div className="miracle-col-compact">
                <div className="miracle-row" style={{ gap: '6px' }}>
                  <label style={{ width: '40px' }}>Time</label>
                  <input
                    className="po-input"
                    value={general.preparationTime}
                    onChange={(e) => updateGeneral('preparationTime', e.target.value)}
                  />
                </div>
              </div>

              {/* Row 2, Col 1: Bill No */}
              <div className="miracle-col-compact">
                <div className="miracle-row" style={{ gap: '6px' }}>
                  <label style={{ width: '75px' }}>Bill No.</label>
                  <input
                    ref={billNoRef}
                    className="po-input po-input-green"
                    value={general.invoiceNo}
                    onChange={(e) => updateGeneral('invoiceNo', e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); saleTypeRef.current?.focus(); }
                      if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) { e.preventDefault(); sellerRef.current?.focus(); }
                    }}
                  />
                </div>
              </div>

              {/* Row 2, Col 2: Sale Type */}
              <div className="miracle-col-compact">
                <div className="miracle-row" style={{ gap: '6px' }}>
                  <label style={{ width: '65px' }}>Sale Type</label>
                  <CreatableSelect
                    ref={saleTypeRef}
                    value={general.saleType}
                    options={saleTypes.map(s => s.saleTypeName || s.sale_type_name)}
                    onChange={(val) => updateGeneral('saleType', val)}
                    onAddOption={(val) => updateGeneral('saleType', val)}
                    onSelect={() => vehicleNoRef.current?.focus()}
                    onPrevField={() => billNoRef.current?.focus()}
                    placeholder="Select Sale Type"
                  />
                </div>
              </div>

            </div>
          </div>



          {/* Transport Details Section - Simplified */}
          <div style={{ borderTop: '1px solid #e1e5e8', paddingTop: '15px', marginTop: '10px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px', marginBottom: '10px' }}>

              <div className="miracle-col-compact">
                <div className="miracle-row" style={{ gap: '6px' }}>
                  <label style={{ width: '75px' }}>Vehical No</label>
                  <input
                    ref={vehicleNoRef}
                    className="po-input"
                    style={{ flex: 1 }}
                    value={general.vehicleNo}
                    onChange={e => updateGeneral('vehicleNo', e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') { e.preventDefault(); lrNoRef.current?.focus(); }
                      if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) { e.preventDefault(); billNoRef.current?.focus(); }
                    }}
                  />
                </div>
              </div>

              <div className="miracle-col-compact">
                <div className="miracle-row" style={{ gap: '6px' }}>
                  <label style={{ width: '60px' }}>L.R. No.</label>
                  <input
                    ref={lrNoRef}
                    className="po-input"
                    value={general.lrNo}
                    onChange={e => updateGeneral('lrNo', e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') { e.preventDefault(); lrDateRef.current?.focus(); }
                      if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) { e.preventDefault(); vehicleNoRef.current?.focus(); }
                    }}
                  />
                </div>
              </div>

              <div className="miracle-col-compact">
                <div className="miracle-row" style={{ gap: '6px' }}>
                  <label style={{ width: '65px' }}>L.R. Date</label>
                  <AirDatePicker
                    ref={lrDateRef}
                    className="po-input"
                    value={general.lrDate}
                    onChange={(val) => updateGeneral('lrDate', val)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') { e.preventDefault(); eWayBillRef.current?.focus(); }
                      if (e.key === 'ArrowLeft') { e.preventDefault(); lrNoRef.current?.focus(); }
                    }}
                  />
                </div>
              </div>

              <div className="miracle-col-compact">
                <div className="miracle-row" style={{ gap: '6px' }}>
                  <label style={{ width: '75px' }}>E-Way Bill</label>
                  <input
                    ref={eWayBillRef}
                    className="po-input"
                    value={general.eWayBillNo}
                    onChange={e => updateGeneral('eWayBillNo', e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (productRows.length > 0) {
                          focusRowCol(productRows[0].id, 'category');
                        }
                      }
                      if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) { e.preventDefault(); lrDateRef.current?.focus(); }
                    }}
                  />
                </div>
              </div>

            </div>
          </div>

          {/* Product Items Table */}
          <div className="po-table-wrapper">
            <table className="po-table">
              <thead>
                <tr>
                  <th style={{ width: '140px', backgroundColor: '#1e3a5f', color: '#fff' }}>Category *</th>
                  <th style={{ width: '250px', backgroundColor: '#1e3a5f', color: '#fff' }}>Product Name *</th>
                  <th style={{ backgroundColor: '#1e3a5f', color: '#fff' }}>HSN Code</th>
                  <th style={{ backgroundColor: '#1e3a5f', color: '#fff' }}>Grade</th>
                  <th style={{ backgroundColor: '#1e3a5f', color: '#fff' }}>Color</th>
                  <th style={{ backgroundColor: '#1e3a5f', color: '#fff' }}>Unit</th>
                  <th style={{ width: '80px', backgroundColor: '#1e3a5f', color: '#fff' }}>Qty</th>
                  <th style={{ width: '100px', backgroundColor: '#1e3a5f', color: '#fff' }}>Rate *</th>
                  <th style={{ width: '120px', backgroundColor: '#1e3a5f', color: '#fff' }}>Amount</th>
                  <th style={{ width: '40px', backgroundColor: '#1e3a5f', color: '#fff' }}></th>
                </tr>
              </thead>
              <tbody>
                {productRows.map((row, idx) => {
                  return (
                    <React.Fragment key={row.id}>
                      <tr key={row.id}>
                        <td>
                          <CreatableSelect
                            ref={el => { ensureRowRef(row.id).category = el; }}
                            value={row.category}
                            options={categoryList}
                            creatable={hasPermission('Category', 'add')}
                            onChange={(val) => updateProductRow(row.id, 'category', val)}
                            onAddOption={(newVal) => { handleAddCategory(newVal); updateProductRow(row.id, 'category', newVal); }}
                            onSelect={() => rowRefs.current[row.id]?.product?.focus()}
                            onPrevField={() => {
                              if (idx === 0) eWayBillRef.current?.focus();
                              else {
                                const prevRowId = productRows[idx - 1].id;
                                rowRefs.current[prevRowId]?.rate?.focus();
                              }
                            }}
                            onTab={() => { termsRef.current?.focus(); }}
                            placeholder="Type Category..."
                            validateOnEnter={true}
                          />
                        </td>
                        <td style={{ position: 'relative' }}>
                          <ProductCreatableSelect
                            ref={el => { ensureRowRef(row.id).product = el; }}
                            value={row.product}
                            products={productList}
                            category={row.category}
                            allowAdd={hasPermission('Product', 'add')}
                            onChange={(val, selected) => {
                              if (val === 'Other') { updateProductRow(row.id, 'product', 'Other'); return; }
                              if (selected) {
                                // Auto-fill fields from product master data
                                updateProductMultipleFields(row.id, {
                                  product: val,
                                  product_id: selected.id || selected._id,
                                  hsnCode: selected.productHsnCode || selected.product_hsn_code || '',
                                  grade: selected.productGrade || selected.product_grade || '',
                                  color: '',   // Color not stored per-product; stays blank for manual entry
                                  rate: selected.productSalePrice || selected.product_sale_price || '',
                                  unit: selected.unit || 'PCS'
                                });
                              } else {
                                updateProductRow(row.id, 'product', val);
                              }
                            }}
                            onSelect={() => rowRefs.current[row.id]?.hsnCode?.focus()}
                            onPrevField={() => rowRefs.current[row.id]?.category?.focus()}
                            onTab={() => { termsRef.current?.focus(); }}
                          />
                        </td>
                        <td>
                          <input
                            ref={el => { ensureRowRef(row.id).hsnCode = el; }}
                            value={row.hsnCode}
                            onChange={e => updateProductRow(row.id, 'hsnCode', e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') { e.preventDefault(); rowRefs.current[row.id]?.grade?.focus(); }
                              if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) { e.preventDefault(); rowRefs.current[row.id]?.product?.focus(); }
                              if (e.key === 'Tab') { e.preventDefault(); termsRef.current?.focus(); }
                            }}
                          />
                        </td>
                        <td>
                          <select
                            ref={el => { ensureRowRef(row.id).grade = el; }}
                            value={row.grade}
                            onChange={e => updateProductRow(row.id, 'grade', e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') { e.preventDefault(); rowRefs.current[row.id]?.color?.focus(); }
                              if (e.key === 'ArrowLeft') { e.preventDefault(); rowRefs.current[row.id]?.hsnCode?.focus(); }
                              if (e.key === 'Tab') { e.preventDefault(); termsRef.current?.focus(); }
                            }}
                          >
                            <option value="">Grade</option>
                            {gradeList.map(g => <option key={g} value={g}>{g}</option>)}
                          </select>
                        </td>
                        <td>
                          <CreatableSelect
                            ref={el => { ensureRowRef(row.id).color = el; }}
                            value={row.color}
                            options={colorList}
                            onChange={(val) => updateProductRow(row.id, 'color', val)}
                            onAddOption={(val) => { handleAddColor(val); updateProductRow(row.id, 'color', val); }}
                            onSelect={() => rowRefs.current[row.id]?.qty?.focus()}
                            onPrevField={() => rowRefs.current[row.id]?.grade?.focus()}
                            onTab={() => { termsRef.current?.focus(); }}
                            placeholder="Color"
                          />
                        </td>
                        <td>
                          <select
                            value={row.unit}
                            onChange={e => updateProductRow(row.id, 'unit', e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') { e.preventDefault(); rowRefs.current[row.id]?.qty?.focus(); }
                              if (e.key === 'ArrowLeft') { e.preventDefault(); rowRefs.current[row.id]?.color?.focus(); }
                              if (e.key === 'Tab') { e.preventDefault(); termsRef.current?.focus(); }
                            }}
                          >
                            <option value="PCS">PCS</option>
                            <option value="BOX">BOX</option>
                            <option value="SET">SET</option>
                            <option value="KGS">KGS</option>
                          </select>
                        </td>
                        <td>
                          <input
                            ref={el => { ensureRowRef(row.id).qty = el; }}
                            type="number"
                            value={row.quantity}
                            onChange={e => updateProductRow(row.id, 'quantity', e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') { e.preventDefault(); rowRefs.current[row.id]?.rate?.focus(); }
                              if (e.key === 'ArrowLeft' && (e.target.selectionStart === 0 || e.target.selectionStart === null)) {
                                e.preventDefault();
                                rowRefs.current[row.id]?.color?.focus();
                              }
                              if (e.key === 'Tab') { e.preventDefault(); termsRef.current?.focus(); }
                            }}
                          />
                        </td>
                        <td>
                          <input
                            ref={el => { ensureRowRef(row.id).rate = el; }}
                            type="number"
                            value={row.rate}
                            onChange={e => updateProductRow(row.id, 'rate', e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                if (idx === productRows.length - 1) {
                                  const newId = addProductRow();
                                  setTimeout(() => focusRowCol(newId, 'category'), 120);
                                } else {
                                  const nextRowId = productRows[idx + 1].id;
                                  focusRowCol(nextRowId, 'category');
                                }
                              }
                              if (e.key === 'Tab') {
                                e.preventDefault();
                                termsRef.current?.focus();
                              }
                              if (e.key === 'ArrowLeft' && (e.target.selectionStart === 0 || e.target.selectionStart === null)) {
                                e.preventDefault();
                                rowRefs.current[row.id]?.qty?.focus();
                              }
                            }}
                          />
                        </td>
                        <td><input value={parseFloat(row.total || 0).toFixed(2)} readOnly style={{ backgroundColor: '#f9fafb' }} /></td>
                        <td>
                          <button type="button" className="po-btn-delete" onClick={() => removeProductRow(row.id)}>✕</button>
                        </td>
                      </tr>

                      {/* Inline Product Form Sub-row */}
                      {row.showNewProduct && (
                        <tr className="inline-product-row">
                          <td colSpan="10">
                            <div className="inline-product-details">
                              <div className="inline-product-title">Add New Product Details</div>
                              <div className="inline-product-grid">
                                <div className="inline-product-field">
                                  <label className="inline-product-label">Name</label>
                                  <input
                                    ref={el => { ensureRowRef(row.id).newName = el; }}
                                    className="inline-product-input"
                                    value={row.newProduct?.name || ''}
                                    onChange={e => updateNewProductField(row.id, 'name', e.target.value)}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') { e.preventDefault(); rowRefs.current[row.id]?.newHsn?.focus(); }
                                    }}
                                  />
                                </div>
                                <div className="inline-product-field">
                                  <label className="inline-product-label">HSN Code</label>
                                  <input
                                    ref={el => { ensureRowRef(row.id).newHsn = el; }}
                                    className="inline-product-input"
                                    value={row.newProduct?.hsn || ''}
                                    onChange={e => updateNewProductField(row.id, 'hsn', e.target.value)}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') { e.preventDefault(); rowRefs.current[row.id]?.newModel?.focus(); }
                                    }}
                                  />
                                </div>
                                <div className="inline-product-field">
                                  <label className="inline-product-label">Model Number</label>
                                  <input
                                    ref={el => { ensureRowRef(row.id).newModel = el; }}
                                    className="inline-product-input"
                                    value={row.newProduct?.modelNumber || ''}
                                    onChange={e => updateNewProductField(row.id, 'modelNumber', e.target.value)}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') { e.preventDefault(); rowRefs.current[row.id]?.newFinish?.focus(); }
                                    }}
                                  />
                                </div>
                                <div className="inline-product-field">
                                  <label className="inline-product-label">Finish Glaze</label>
                                  <input
                                    ref={el => { ensureRowRef(row.id).newFinish = el; }}
                                    className="inline-product-input"
                                    value={row.newProduct?.finishGlaze || ''}
                                    onChange={e => updateNewProductField(row.id, 'finishGlaze', e.target.value)}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') { e.preventDefault(); rowRefs.current[row.id]?.newPrice?.focus(); }
                                    }}
                                  />
                                </div>
                                <div className="inline-product-field">
                                  <label className="inline-product-label">Sale Price</label>
                                  <input
                                    ref={el => { ensureRowRef(row.id).newPrice = el; }}
                                    className="inline-product-input"
                                    type="number"
                                    value={row.newProduct?.salePrice || ''}
                                    onChange={e => updateNewProductField(row.id, 'salePrice', e.target.value)}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        // Sync name, hsn and auto-fill rate with salePrice
                                        const hsn = row.newProduct?.hsn || '';
                                        const productName = row.newProduct?.name || 'Other';
                                        const salePrice = row.newProduct?.salePrice || '';
                                        updateProductMultipleFields(row.id, {
                                          product: productName,
                                          hsnCode: hsn,
                                          rate: salePrice,
                                          showNewProduct: false
                                        });
                                        // Focus grade in main row
                                        setTimeout(() => rowRefs.current[row.id]?.grade?.focus(), 100);
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot className="po-tfoot">
                <tr>
                  <td colSpan="6" style={{ textAlign: 'left' }}>Total Entry: {productRows.length}</td>
                  <td style={{ textAlign: 'right', paddingRight: '20px' }}>{totalQtyCalc.toFixed(3)}</td>
                  <td style={{ textAlign: 'right', fontWeight: '600', color: '#64748b', paddingRight: '10px' }}>Sub Total</td>
                  <td style={{ textAlign: 'right', paddingRight: '10px', fontWeight: '600' }}>{subTotalCalc.toFixed(2)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <button type="button" className="po-btn-add" onClick={addProductRow}>+ Add Row</button>

          {/* BOTTOM SUMMARY SECTION - RESTRUCTURED FOR PRECISION ALIGNMENT */}
          <div className="po-bottom-section-grid" style={{
            display: 'grid',
            gridTemplateColumns: '1.2fr 1fr',
            gridAutoRows: 'auto',
            gap: '15px 40px',
            marginTop: '30px',
            alignItems: 'stretch'
          }}>

            {/* ROW 1: Terms & Conditions and Top Summary */}
            <div className="miracle-col-compact" style={{ display: 'flex', flexDirection: 'column' }}>
              <label className="po-label">Terms &amp; Conditions</label>
              <textarea
                ref={termsRef}
                className="po-input"
                style={{ flex: 1, resize: 'none', border: '1px solid #e2e8f0', minHeight: '80px' }}
                value={general.termsAndConditions}
                onChange={e => updateGeneral('termsAndConditions', e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); remarksRef.current?.focus(); }
                  if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) { e.preventDefault(); focusRowCol(productRows.length - 1, 'rate'); }
                }}
              />
            </div>

            <div className="po-summary-section">
              <div className="summary-row">
                <label>Discount (%)</label>
                <div style={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
                  <input
                    ref={discountRef}
                    className="po-input"
                    type="number"
                    style={{ width: '80px', height: '24px' }}
                    value={general.discountPercent}
                    onChange={e => updateGeneral('discountPercent', e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') { e.preventDefault(); insuranceRef.current?.focus(); }
                      if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) { e.preventDefault(); remarksRef.current?.focus(); }
                    }}
                  />
                  <span style={{ fontSize: '11px', color: '#64748b', marginLeft: '5px', fontWeight: 'bold' }}>% (-)</span>
                  <div className="summary-value" style={{ width: '100px', marginLeft: '10px', color: '#ef4444' }}>-{discountAmountCalc.toFixed(2)}</div>
                </div>
              </div>
              <div className="summary-row">
                <label>Insurance (%)</label>
                <div style={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
                  <input
                    ref={insuranceRef}
                    className="po-input"
                    type="number"
                    style={{ width: '80px', height: '24px' }}
                    value={general.insurancePercent}
                    onChange={e => updateGeneral('insurancePercent', e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') { e.preventDefault(); freightRef.current?.focus(); }
                      if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) { e.preventDefault(); remarksRef.current?.focus(); }
                    }}
                  />
                  <div className="summary-value" style={{ width: '100px', marginLeft: '10px' }}>{insuranceAmountCalc.toFixed(2)}</div>
                </div>
              </div>
              <div className="summary-row">
                <label>Freight (%)</label>
                <div style={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
                  <input
                    ref={freightRef}
                    className="po-input"
                    type="number"
                    style={{ width: '80px', height: '24px' }}
                    value={general.freightPercent}
                    onChange={e => updateGeneral('freightPercent', e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') { e.preventDefault(); extraChargesRef.current?.focus(); }
                      if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) { e.preventDefault(); insuranceRef.current?.focus(); }
                    }}
                  />
                  <div className="summary-value" style={{ width: '100px', marginLeft: '10px' }}>{freightAmountCalc.toFixed(2)}</div>
                </div>
              </div>
              <div className="summary-row">
                <label>Extra Charges</label>
                <div style={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
                  <input
                    ref={extraChargesRef}
                    className="po-input"
                    type="number"
                    style={{ width: '80px', height: '24px' }}
                    value={general.extraChargesAmount}
                    onChange={e => updateGeneral('extraChargesAmount', e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') { e.preventDefault(); saveBtnRef.current?.focus(); }
                      if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) { e.preventDefault(); freightRef.current?.focus(); }
                    }}
                  />
                  <div className="summary-value" style={{ width: '100px', marginLeft: '10px' }}>{extraChargesAmountCalc.toFixed(2)}</div>
                </div>
              </div>
              <div className="summary-row bold">
                <label>Taxable Amount</label>
                <div className="summary-value">{taxableValCalc.toFixed(2)}</div>
              </div>
            </div>

            {/* ROW 2: Remarks and Bottom Summary */}
            <div className="miracle-col-compact" style={{ display: 'flex', flexDirection: 'column' }}>
              <label className="po-label">Remarks</label>
              <textarea
                ref={remarksRef}
                className="po-input"
                style={{ flex: 1, resize: 'none', border: '1px solid #e2e8f0', minHeight: '40px' }}
                value={general.remarks}
                onChange={e => updateGeneral('remarks', e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); discountRef.current?.focus(); }
                  if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) { e.preventDefault(); termsRef.current?.focus(); }
                }}
              />
            </div>

            <div className="po-summary-section">
              <div className="summary-row">
                <label>Central Tax ({tax1Rate}%)</label>
                <div className="summary-value">{tax1AmountCalc.toFixed(2)}</div>
              </div>
              <div className="summary-row">
                <label>State/UT Tax ({tax2Rate}%)</label>
                <div className="summary-value">{tax2AmountCalc.toFixed(2)}</div>
              </div>
              <div className="summary-row grand-total">
                <label>Bill Amount</label>
                <div className="summary-value" style={{ fontSize: '22px' }}>{grandTotalCalc.toFixed(2)}</div>
              </div>
            </div>

            {/* ROW 3: Buttons Bottom Right */}
            <div />
            <div className="po-actions" style={{ display: 'flex', gap: '10px', marginTop: '10px', justifyContent: 'flex-end' }}>
              <button type="button" className="po-btn-cancel" onClick={onClose} style={{ width: '120px', height: '40px', fontWeight: 'bold' }}>CANCEL</button>
              <button
                ref={saveBtnRef}
                type="submit"
                className="po-btn-submit"
                style={{ width: '250px', height: '40px', fontSize: '15px' }}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); saveBtnRef.current?.click(); } }}
              >
                {isEdit ? 'UPDATE' : 'SAVE'}
              </button>
            </div>

          </div>
        </form>
      </div>


      {/* Add Seller Modal */}
      <AddSellerModal
        isOpen={showAddSellerModal}
        onClose={() => setShowAddSellerModal(false)}
        onSuccess={(newSeller) => {
          setSellers(prev => [...prev, newSeller]);
          updateGeneral('seller', newSeller.sellerTradeName);
          setShowAddSellerModal(false);
          // Focus next field after adding
          setTimeout(() => invoiceNameRef.current?.focus(), 100);
        }}
      />
    </div>
  );

  if (isPage) return formContent;

  return (
    <div className="modal-overlay" onClick={onClose}>
      {formContent}
    </div>
  );
}

export default AddPurchaseOrderForm;

